'use client';

import { Input } from '@savvy-kit/components/input';
import { Label } from '@savvy-kit/components/label';
import { cn } from '@savvy-kit/lib/utils';

import type { TimeFilterMeta, TimeOperator } from '../../types/filter-meta';
import type { TimeFilterValue } from '../../types/filter-values';
import { useOperatorOptions } from './hooks/use-operator-options';
import { FilterActions } from './shared/filter-actions';
import { FilterOperatorSelect } from './shared/filter-operator-select';

export type TimeFilterProps = {
  readonly filterMeta?: TimeFilterMeta;
  readonly value: TimeFilterValue | undefined;
  readonly onChange: (value: TimeFilterValue | undefined) => void;
  readonly className?: string;
};

export function TimeFilter({ filterMeta, value, onChange, className }: TimeFilterProps) {
  const operators = useOperatorOptions('time', filterMeta);
  const operator: TimeOperator = value?.operator ?? 'between';

  const singleValue = value && value.operator !== 'isEmpty' && value.operator !== 'between' ? value.value : '';
  const startValue = value && value.operator === 'between' ? value.start : '';
  const endValue = value && value.operator === 'between' ? value.end : '';

  return (
    <div className={cn('flex flex-col gap-3 p-3', className)}>
      <FilterOperatorSelect
        id="time-filter-operator"
        value={operator}
        options={operators}
        onChange={(op) => (op === 'isEmpty' ? onChange({ operator: 'isEmpty' }) : onChange(undefined))}
      />

      {operator !== 'isEmpty' && operator !== 'between' && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="time-filter-time">Time</Label>
          <Input
            id="time-filter-time"
            type="time"
            value={singleValue}
            onChange={(e) => onChange(e.target.value ? { operator, value: e.target.value } : undefined)}
          />
        </div>
      )}

      {operator === 'between' && (
        <div className="flex items-end gap-2">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="time-filter-start">From</Label>
            <Input
              id="time-filter-start"
              type="time"
              value={startValue}
              onChange={(e) =>
                onChange(
                  e.target.value
                    ? { operator: 'between', start: e.target.value, end: endValue || e.target.value }
                    : undefined,
                )
              }
            />
          </div>
          <span className="pb-2 text-sm text-muted-foreground">–</span>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="time-filter-end">To</Label>
            <Input
              id="time-filter-end"
              type="time"
              value={endValue}
              onChange={(e) =>
                onChange(
                  e.target.value
                    ? { operator: 'between', start: startValue || e.target.value, end: e.target.value }
                    : undefined,
                )
              }
            />
          </div>
        </div>
      )}

      {value && <FilterActions onClear={() => onChange(undefined)} />}
    </div>
  );
}
